import { useEffect, useRef, useState } from 'react';
import type { TouchEvent } from 'react';
import { Link } from 'react-router-dom';
import type { Film } from '@/types/film';
import { typeLabel } from '@/lib/film';

interface HeroCarouselProps {
  items: Film[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (paused || items.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused, items.length]);

  if (items.length === 0) return null;
  const film = items[index];

  const go = (direction: number) => {
    setIndex((prev) => (prev + direction + items.length) % items.length);
  };

  const onTouchStart = (event: TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const onTouchEnd = (event: TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 48) go(delta < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  return (
    <section
      className="relative h-[560px] w-full overflow-hidden bg-primary-950 md:h-[640px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div key={film.id} className="absolute inset-0 overflow-hidden">
        <img
          src={film.backdrop || film.cover}
          alt={`${film.title} 焦点图`}
          className="animate-slow-zoom h-full w-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/92 via-primary-950/70 to-primary-950/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/40" />

      <div className="relative mx-auto flex h-full w-full max-w-portal flex-col justify-end px-4 pb-16 pt-28 md:px-6 md:pb-20">
        <div key={`${film.id}-text`} className="animate-fade-up max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-accent-300/40 bg-accent-500/15 px-3 py-1 text-xs font-medium text-accent-200">
            <i className="ri-fire-line text-sm" />
            本季焦点推荐
          </span>
          <h2 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
            {film.title}
          </h2>
          <p className="mt-2 text-sm tracking-wide text-background-300 md:text-base">{film.enTitle}</p>

          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-200">
            <span className="rounded-md bg-background-50/10 px-2 py-0.5 text-xs">{typeLabel(film.type)}</span>
            <span>{film.year}</span>
            <span className="h-3 w-px bg-background-400/50" />
            <span>{film.genres.join(' / ')}</span>
            <span className="h-3 w-px bg-background-400/50" />
            <span>{film.region}</span>
            <span className="flex items-center gap-1 font-semibold text-accent-300">
              <i className="ri-star-fill text-xs" />
              {film.rating.toFixed(1)}
            </span>
          </div>

          <p className="mt-4 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-300 md:text-[15px]">
            {film.synopsis}
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${film.id}`}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
            >
              <i className="ri-play-circle-line text-lg" />
              查看详情
            </Link>
            <Link
              to={`/category?type=${film.type}&genre=${encodeURIComponent(film.genres[0])}`}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-2.5 text-sm text-background-50 transition-colors hover:border-background-50/70 hover:bg-background-50/10"
            >
              更多{film.genres[0]}作品
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>

        <div className="absolute bottom-8 right-4 hidden items-center gap-2 md:flex md:right-6">
          {items.map((item, itemIndex) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到 ${item.title}`}
              onClick={() => setIndex(itemIndex)}
              className={`h-1 cursor-pointer rounded-full transition-all duration-300 ${
                itemIndex === index ? 'w-10 bg-accent-400' : 'w-5 bg-background-50/35 hover:bg-background-50/60'
              }`}
            />
          ))}
          <span className="ml-3 font-heading text-sm text-background-300">
            {String(index + 1).padStart(2, '0')} / {String(items.length).padStart(2, '0')}
          </span>
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="上一张"
            className="ml-2 flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-50/30 text-background-100 transition-colors hover:bg-background-50/15"
          >
            <i className="ri-arrow-left-s-line" />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="下一张"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-50/30 text-background-100 transition-colors hover:bg-background-50/15"
          >
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>
      </div>

      <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2 md:hidden">
        {items.map((item, itemIndex) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到 ${item.title}`}
            onClick={() => setIndex(itemIndex)}
            className={`h-1 cursor-pointer rounded-full transition-all ${
              itemIndex === index ? 'w-8 bg-accent-400' : 'w-4 bg-background-50/35'
            }`}
          />
        ))}
      </div>
    </section>
  );
}